// RoadmapDrawer — in-page picker for the 100-step AI Mastery Path.
//
// Lives inside the home hero so tapping "Roadmap to AI Mastery" no longer
// navigates away from the home chat. Tapping any skill closes the drawer
// and calls `onLaunchSkill`, which the hero wires up to its existing
// `handleSkillClick` flow (seed query + open `UnifiedChatShell` inline).
//
// We still expose a small "Open full page" link at the top so power users
// who want the dedicated /roadmap page can get there with one extra tap.
import { Link } from "react-router-dom";
import { ExternalLink, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";
import {
  AI_MASTERY_PATH,
  MASTERY_TIERS,
  type MasterySkill,
  type MasteryTier,
} from "@/data/aiMasteryPath";
import HackathonCTA from "@/components/hackathon/HackathonCTA";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLaunchSkill: (skill: MasterySkill) => void;
};

const TIER_ORDER: MasteryTier[] = [
  "Awareness",
  "Application",
  "Leverage",
  "Systems",
  "Architecture",
];

export default function RoadmapDrawer({ open, onOpenChange, onLaunchSkill }: Props) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto p-0"
      >
        <SheetHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-xl border-b border-border px-5 py-4 text-left">
          <div className="flex items-start justify-between gap-3">
            <div>
              <SheetTitle className="font-mono text-base">
                Roadmap to <span className="text-primary">AI Mastery</span>
              </SheetTitle>
              <SheetDescription className="font-mono text-[11px] mt-1">
                Tap any skill to launch it in the chat — right here, no page change.
              </SheetDescription>
            </div>
            <SheetClose
              aria-label="Close roadmap"
              className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <X className="w-4 h-4" />
            </SheetClose>
          </div>
          <Link
            to="/roadmap"
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors mt-2 self-start"
          >
            Open full page <ExternalLink className="w-2.5 h-2.5" />
          </Link>
        </SheetHeader>

        <div className="px-5 py-5 space-y-6">
          <HackathonCTA variant="compact" onNavigate={() => onOpenChange(false)} />
          {TIER_ORDER.map((tier) => {
            const meta = MASTERY_TIERS[tier];
            const skills = AI_MASTERY_PATH.filter((s) => s.tier === tier);
            return (
              <section key={tier}>
                <header className="flex items-baseline gap-2 mb-2">
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-mono uppercase tracking-wider ${meta.pill}`}
                  >
                    <span className={`w-1 h-1 rounded-full ${meta.dot}`} aria-hidden />
                    {meta.headline}
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {meta.range} · {meta.blurb}
                  </span>
                </header>
                <ul className="grid grid-cols-1 gap-1">
                  {skills.map((s) => (
                    <li key={s.num}>
                      <button
                        type="button"
                        onClick={() => {
                          onLaunchSkill(s);
                          onOpenChange(false);
                        }}
                        className="group w-full flex items-center gap-2 rounded-md border border-transparent hover:border-primary/40 hover:bg-card px-2 py-1.5 text-left transition-colors"
                      >
                        <span className="text-[10px] font-mono text-muted-foreground w-7 shrink-0 tabular-nums">
                          #{s.num}
                        </span>
                        <span className="text-xs font-mono text-foreground group-hover:text-primary transition-colors">
                          {s.label}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}